import { DragDropContext, Draggable, Droppable, type OnDragEndResponder } from "@hello-pangea/dnd"
import {
	Checkbox,
	Heading,
	IconButton,
	LuIcon,
	Menu,
	Popover,
	radiusCva,
	Separator,
	Switch,
	Text,
	toast,
	useProvider,
} from "@overmap-ai/blocks"
import { ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError, selectProjectFiles } from "@overmap-ai/core"
import * as RadixToggleGroup from "@radix-ui/react-toggle-group"
import * as turf from "@turf/turf"
import { cx } from "class-variance-authority"
import JSZip from "jszip"
import { LngLatBounds, type LngLatLike } from "mapbox-gl"
import { type ChangeEventHandler, type ComponentPropsWithoutRef, memo, useCallback, useMemo } from "react"
import { toWgs84 } from "reproject"
import { read } from "shapefile"
import { twMerge } from "tailwind-merge"

import { useImageOverlayPicker, usePolygonPicker } from "../../contexts/hooks"
import { useFileInputRef } from "../../hooks/utils"
import { useCurrentMap } from "../../mapbox/hooks"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { setHiddenProjectFileIds, setShowProjectBounds, useAppDispatch, useAppSelector } from "../../store"
import { selectActiveProject, selectHiddenProjectFileIds, selectShowProjectBounds } from "../../store/selectors"
import { readFileAsArrayBuffer } from "../../utils/file"
import { MapControl } from "../MapVisibilityControl/MapControl"
import { ProjectFileVisibilityItem } from "../MapVisibilityControl/VisibilityItems/ProjectFileVisibilityItem"

export type ProjectFileVisibilityPopoverProps = ComponentPropsWithoutRef<typeof Popover.Root> &
	Pick<ComponentPropsWithoutRef<typeof Popover.Content>, "align" | "size" | "side"> & {
		accentColor?: ComponentPropsWithoutRef<typeof Text>["accentColor"]
	}

export const ProjectFileVisibilityPopover = memo<ProjectFileVisibilityPopoverProps>((props) => {
	const { children, open, onOpenChange, defaultOpen, modal, size = "md", align, side = "top" } = props

	const activeProject = useAppSelector(selectActiveProject)

	if (!activeProject) {
		throw new Error(`Expected an active project to be set, but got ${activeProject}`)
	}

	// TODO: Should select based on projectId
	const projectFiles = useAppSelector(selectProjectFiles)
	const hiddenProjectFileIds = useAppSelector(selectHiddenProjectFileIds)
	const showProjectBounds = useAppSelector(selectShowProjectBounds)
	const dispatch = useAppDispatch()
	const [fileInputRef, handleToggleFileInput] = useFileInputRef()
	const [directoryInputRef, handleToggleDirectoryInput] = useFileInputRef()
	const { radius } = useProvider()
	const map = useCurrentMap()
	const openImageOverlayPicker = useImageOverlayPicker()
	const openPolygonPicker = usePolygonPicker()

	const convertAndSaveShapefileAsGeoJSON = useCallback(
		async (shpFile: File, dbfFile: File | null, prjFile: File | null) => {
			try {
				const [shpBuffer, dbfBuffer, prjString] = await Promise.all([
					readFileAsArrayBuffer(shpFile),
					dbfFile ? readFileAsArrayBuffer(dbfFile) : null,
					prjFile?.text(),
				])
				const featureCollection = await read(shpBuffer, dbfBuffer)
				const convertedFeatures = featureCollection.features.map((feature) => ({
					...feature,
					...(prjString && { geometry: toWgs84(feature.geometry, prjString) }),
				}))
				const geoJSONString = JSON.stringify({ type: "FeatureCollection", features: convertedFeatures })
				const name = `${shpFile.name.split(".")[0]}.geojson`
				const blob = new Blob([geoJSONString], { type: "geojson" })
				const geoJSONFile = new File([blob], name, { type: "geojson" })
				return geoJSONFile
			} catch (error) {
				console.error("Error converting shapefile to GeoJSON:", error)
				toast.error({
					title: "Failed to upload shapefile as GeoJSON",
					description: "Please try again later.",
				})
			}
		},
		[],
	)

	const handleFileSelected = useCallback<ChangeEventHandler<HTMLInputElement>>(
		async (event) => {
			if (!map) return

			const files = Array.from(event.target.files ?? [])
			if (files.length === 0) return
			const file = files[0]!

			const zipObjectToFile = async (zipObject: JSZip.JSZipObject) => {
				const data = await zipObject.async("blob")
				return new File([data], zipObject.name)
			}

			if (file.type.includes("zip")) {
				const zip = new JSZip()
				try {
					const zipContents = await zip.loadAsync(file)
					let shpFile: File | null = null
					let dbfFile: File | null = null
					let prjFile: File | null = null
					for (const zipObject of Object.values(zipContents.files)) {
						if (!zipObject.dir) {
							const fileExtension = zipObject.name.split(".").pop()?.toLowerCase()
							switch (fileExtension) {
								case "shp":
									shpFile = await zipObjectToFile(zipObject)
									break
								case "dbf":
									dbfFile = await zipObjectToFile(zipObject)
									break
								case "prj":
									prjFile = await zipObjectToFile(zipObject)
									break
								default:
							}
						}
					}
					if (!shpFile) {
						toast.error({
							title: "Failed to find .shp file",
							description: "Please ensure there is a .shp file in the zip file.",
						})
						return
					}
					const convertedFile = await convertAndSaveShapefileAsGeoJSON(shpFile, dbfFile, prjFile)

					if (!convertedFile) return

					void sdk.projectFiles.add({
						file: convertedFile,
						project: activeProject.uuid,
						z_index: projectFiles.length,
						bounds: turf.multiPoint(map.getCenter().toBounds(1000).toArray()).geometry,
					})
				} catch (error) {
					console.error("Error unzipping file:", error)
					toast.error({
						title: "Failed to unzip zip file",
						description: "Please try again later.",
					})
				}
			} else {
				const initialBounds = new LngLatBounds(
					turf.multiPoint(map.getCenter().toBounds(1000).toArray()).geometry.coordinates as [
						LngLatLike,
						LngLatLike,
					],
				)

				openImageOverlayPicker((close) => ({
					initialBounds: initialBounds,
					file: file,
					onConfirm: (bounds) => {
						close()
						void sdk.projectFiles.add({
							file: file,
							project: activeProject.uuid,
							z_index: projectFiles.length,
							bounds: turf.multiPoint(bounds.toArray()).geometry,
						})
					},
					onCancel: close,
				}))
			}
		},
		[convertAndSaveShapefileAsGeoJSON, openImageOverlayPicker, map, projectFiles.length, activeProject.uuid],
	)

	const handleDirectorySelected = useCallback<ChangeEventHandler<HTMLInputElement>>(
		async (event) => {
			if (!map) return

			const files = Array.from(event.target.files ?? [])
			if (files.length === 0) return

			try {
				let shpFile: File | null = null
				let dbfFile: File | null = null
				let prjFile: File | null = null
				for (const file of files) {
					const fileExtension = file.name.split(".").pop()?.toLowerCase()
					switch (fileExtension) {
						case "shp":
							shpFile = file
							break
						case "dbf":
							dbfFile = file
							break
						case "prj":
							prjFile = file
							break
					}
				}

				if (!shpFile) {
					toast.error({
						title: "Failed to find .shp file",
						description: "Please ensure there is a .shp file in the directory.",
					})
					return
				}
				const convertedFile = await convertAndSaveShapefileAsGeoJSON(shpFile, dbfFile, prjFile)

				if (!convertedFile) return

				void sdk.projectFiles.add({
					file: convertedFile,
					project: activeProject.uuid,
					z_index: projectFiles.length,
					bounds: turf.multiPoint(map.getCenter().toBounds(1000).toArray()).geometry,
				})
			} catch (e: unknown) {
				toast.error({
					title: "Failed to unzip zip file",
					description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
				})
			}
		},
		[convertAndSaveShapefileAsGeoJSON, projectFiles.length, map, activeProject.uuid],
	)

	const handleDragEnd = useCallback<OnDragEndResponder>(
		(result) => {
			const { source, destination } = result
			if (!destination) return
			const newProjectFiles = [...projectFiles]
			const [removed] = newProjectFiles.splice(source.index, 1)
			newProjectFiles.splice(destination.index, 0, removed)
			newProjectFiles.forEach((item, index) => {
				if (item.z_index !== index) {
					void sdk.projectFiles.update({
						uuid: item.uuid,
						z_index: index,
					})
				}
			})
		},
		[projectFiles],
	)

	const handleEditBounds = useCallback(() => {
		openPolygonPicker((close) => ({
			initialPolygon: activeProject.bounds,
			onConfirm: (polygon) => {
				sdk.projects.update({ uuid: activeProject.uuid, bounds: polygon }).catch((error) => {
					toast.error({
						title: "Failed to update project bounds",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
				close()
			},
			onCancel: close,
		}))
	}, [activeProject.uuid, activeProject.bounds, openPolygonPicker])

	const allProjectFileIds = useMemo(() => projectFiles.map((projectFile) => projectFile.uuid), [projectFiles])

	const visibleProjectFileIds = useMemo(() => {
		return allProjectFileIds.filter((uuid) => !(uuid in hiddenProjectFileIds))
	}, [allProjectFileIds, hiddenProjectFileIds])

	const handleToggleHiddenProjectFileIds = useCallback(() => {
		dispatch(
			setHiddenProjectFileIds(visibleProjectFileIds.length === allProjectFileIds.length ? allProjectFileIds : []),
		)
	}, [allProjectFileIds, dispatch, visibleProjectFileIds.length])

	const handleProjectBoundCheckedChange = useCallback(
		(checked: boolean) => {
			dispatch(setShowProjectBounds(checked))
		},
		[dispatch],
	)

	return (
		<Popover.Root open={open} onOpenChange={onOpenChange} defaultOpen={defaultOpen} modal={modal}>
			<Popover.Trigger asChild>{children}</Popover.Trigger>
			<Popover.Content size={size} align={align} side={side} className="max-w-xs">
				<div className="flex w-full flex-col gap-4">
					<section className="flex flex-col gap-2">
						<div className="flex w-full items-center justify-between">
							<Heading size="sm">Overlays</Heading>
							<Menu.Root>
								<Menu.ClickTrigger>
									<IconButton size="sm" variant="surface">
										<LuIcon icon={modelIconMapping.add} />
									</IconButton>
								</Menu.ClickTrigger>
								<Menu.Content size="sm">
									<Menu.Item onSelect={handleToggleFileInput}>
										<LuIcon icon={modelIconMapping.file} />
										File
									</Menu.Item>
									<Menu.Item onSelect={handleToggleDirectoryInput}>
										<LuIcon icon={modelIconMapping.folder} />
										Directory (shapefiles)
									</Menu.Item>
								</Menu.Content>
							</Menu.Root>
						</div>
						{projectFiles.length > 0 ? (
							<>
								<div
									onClick={handleToggleHiddenProjectFileIds}
									className={twMerge(
										cx(
											"hover:bg-(--base-a3) active:bg-(--base-a4) group flex cursor-pointer items-center gap-2 px-2 py-1 transition-[background,opacity]",
											radiusCva({ radius, maxLarge: true }),
											{
												"bg-(--base-a3)":
													visibleProjectFileIds.length === allProjectFileIds.length,
											},
										),
									)}
									tabIndex={0}
								>
									<div className="grow">
										<div className="flex items-center gap-2">
											<Checkbox.Root
												size="xs"
												checked={visibleProjectFileIds.length === allProjectFileIds.length}
											>
												<Checkbox.Indicator>
													<LuIcon icon={modelIconMapping.check} />
												</Checkbox.Indicator>
											</Checkbox.Root>
											<Text size="sm">Show all</Text>
										</div>
									</div>
								</div>
								<DragDropContext onDragEnd={handleDragEnd}>
									<Droppable droppableId="PROJECT_FILES">
										{(droppableProvided) => (
											<RadixToggleGroup.Root
												type="multiple"
												value={visibleProjectFileIds}
												asChild
											>
												<ol
													className="flex flex-col gap-1"
													ref={droppableProvided.innerRef}
													{...droppableProvided.droppableProps}
												>
													{projectFiles.map((projectFile, index) => {
														const { uuid } = projectFile
														return (
															<Draggable draggableId={uuid} index={index} key={uuid}>
																{(draggableProvided) => (
																	<ProjectFileVisibilityItem
																		projectFileId={uuid}
																		ref={draggableProvided.innerRef}
																		{...draggableProvided.draggableProps}
																		{...draggableProvided.dragHandleProps}
																	/>
																)}
															</Draggable>
														)
													})}
													{droppableProvided.placeholder}
												</ol>
											</RadixToggleGroup.Root>
										)}
									</Droppable>
								</DragDropContext>
							</>
						) : (
							<div className="flex w-full">
								<Text accentColor="base" weight="regular" size="sm">
									No overlays.
								</Text>
							</div>
						)}
					</section>

					<input
						className="hidden"
						type="file"
						ref={fileInputRef}
						accept="image/png,image/jpeg,image/jpg,image/gif,image/bmp,image/tiff,image/svg+xml,image/webp,.geojson,application/zip"
						onChange={handleFileSelected}
					/>
					<input
						className="hidden"
						type="file"
						ref={directoryInputRef}
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						webkitdirectory=""
						multiple
						onChange={handleDirectorySelected}
					/>

					<Separator size="full" />

					<section className="flex flex-col gap-2">
						<Heading size="sm">Map theme</Heading>
						<MapControl />
					</section>

					<Separator size="full" />

					<section className="flex items-center justify-between gap-2">
						<div className="flex items-center gap-2">
							<Heading size="sm">Project bounds</Heading>
							<IconButton onClick={handleEditBounds} variant="ghost" accentColor="base" size="sm">
								<LuIcon icon={modelIconMapping.edit} />
							</IconButton>
						</div>
						<Switch.Root
							checked={showProjectBounds}
							onCheckedChange={handleProjectBoundCheckedChange}
							size="xs"
							radius="full"
						>
							<Switch.Thumb />
						</Switch.Root>
					</section>
				</div>
			</Popover.Content>
		</Popover.Root>
	)
})
ProjectFileVisibilityPopover.displayName = "ProjectFileVisibilityPopover"
